import { useEffect, useRef } from "react";
import { Button, Chip } from "@heroui/react";
import { EmptyState, ErrorState, SkeletonStorefront } from "@branding/ui";
import gsap from "gsap";
import useSWR from "swr";
import { ProductCard } from "../components/ProductCard";
import { JsonLd } from "../components/JsonLd";
import { fetchProducts } from "../lib/api";
import { displaySku } from "../lib/display-sku";
import { formatIdr } from "../lib/format";
import { homeJsonLd, productPath } from "../lib/json-ld";
import { publicOrigin } from "../lib/format";
import { useSsrData } from "../lib/ssr-data";
import type { Product } from "../lib/types";

export function HomePage() {
  const ssr = useSsrData();
  const fallback = ssr?.kind === "home" ? ssr.products : undefined;
  const { data, error, isLoading, mutate } = useSWR<Product[]>(
    "products",
    fetchProducts,
    { fallbackData: fallback },
  );
  const heroRef = useRef<HTMLElement | null>(null);
  const products = data ?? [];
  const lead = products[0];
  const leadSku = lead ? displaySku(lead) : null;

  useEffect(() => {
    const hero = heroRef.current;
    if (!hero) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    gsap.fromTo(
      hero,
      {
        clipPath: "inset(0% 0% 0% 0%)",
        filter: "blur(0px)",
      },
      {
        clipPath: "inset(0% 0% 0% 0%)",
        filter: "blur(0px)",
        boxShadow:
          "0 18px 40px color-mix(in oklab, var(--foreground) 18%, transparent)",
        duration: 0.7,
        ease: "expo.out",
      },
    );
  }, [lead?.id]);

  if (error) {
    return (
      <main className="mx-auto max-w-6xl px-4 py-12 md:px-6">
        <ErrorState
          title="Katalog tidak bisa dibuka"
          description={error instanceof Error ? error.message : undefined}
          onRetry={() => {
            void mutate();
          }}
        />
      </main>
    );
  }

  if (isLoading && !products.length) {
    return (
      <main className="mx-auto max-w-6xl px-4 py-12 md:px-6">
        <SkeletonStorefront />
      </main>
    );
  }

  return (
    <main>
      <JsonLd data={homeJsonLd(products, publicOrigin())} />
      <section
        ref={heroRef}
        className="pasar-stall mx-auto mt-6 grid w-full max-w-6xl overflow-hidden rounded-3xl md:grid-cols-[1.2fr_0.8fr]"
      >
        <div className="flex flex-col justify-end gap-5 bg-content1 px-6 py-10 md:px-10 md:py-14">
          <h1 className="max-w-[16ch] text-4xl font-semibold tracking-tight md:text-6xl">
            Pasar Kupang
          </h1>
          {lead && leadSku ? (
            <>
              <p className="max-w-prose text-base text-muted md:text-lg">
                {lead.name} dari {lead.merchant.name}. Harga dan stok yang
                tertulis adalah harga jual.
              </p>
              <div className="flex flex-wrap items-end gap-4">
                <p className="pasar-price text-3xl font-semibold">
                  {formatIdr(leadSku.price)}
                </p>
                <Chip
                  color={leadSku.available > 0 ? "success" : "danger"}
                  variant="soft"
                >
                  <Chip.Label>
                    {leadSku.available > 0 ? "Tersedia" : "Habis"}
                  </Chip.Label>
                </Chip>
              </div>
              <div>
                <Button
                  variant="primary"
                  onPress={() => window.location.assign(productPath(lead.slug))}
                >
                  Lihat {lead.name}
                </Button>
              </div>
            </>
          ) : (
            <p className="max-w-prose text-base text-muted">
              Etalase UMKM Kota Kupang. Tenun, kuliner, dan hasil kebun yang
              bisa dibawa pulang.
            </p>
          )}
        </div>
        <div className="pasar-weave min-h-48 md:min-h-full" aria-hidden="true" />
      </section>

      <section className="mx-auto w-full max-w-6xl px-4 py-12 md:px-6">
        <h2 className="mb-6 text-2xl font-semibold">Sedang dijual</h2>
        {products.length === 0 ? (
          <EmptyState
            title="Belum ada produk"
            description="Katalog publik masih kosong."
            action={
              <Button variant="primary" onPress={() => window.location.assign("/pasar")}>
                Muat ulang
              </Button>
            }
          />
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {products.map((product) => (
              <ProductCard key={product.id} product={product} />
            ))}
          </div>
        )}
      </section>
    </main>
  );
}
